
import axios from "@/services/axios";
import { getLogoPath } from "@/locales/i18n";
import ResearchCategoryFiltersData from "@/modules/research-category-filters-data";
import ResearchOrganizationRepositoryAPI from "@/modules/research-organization/research-organization-repository-api";
import { defineComponent, reactive, inject, computed } from "vue";
import { AxiosResponse } from "axios";
import { useI18n } from "vue-i18n";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { getISODateString } from "@/utils/date-view-format";
import { location } from "@/utils/default-data";

interface UserRegister {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  researchOrganizationId: string;
  researcherStatusId: string;
  createdDate: string;
  username: string;
  password: string;
  passwordRepeat: string;
  GDPRConsent: GDPR;
  External: boolean;
  researchInstitutionMSTID: string;
}

type GDPR = "APPROVED" | "DECLINED";

interface ResearchOrganizationRegister {
  name: string;
  number: number;
  websiteURL: string;
  cityId: string;
}

export default defineComponent({
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const { t } = useI18n();
    const toast: any = inject("toast");
    const keycloak = inject("keycloak") as Keycloak.KeycloakInstance;
    const state = reactive({
      gdprConsentEnabled: false,
      consentEnabled: false,
      showOrganizationForm: false,
      user: {
        GDPRConsent: "DECLINED",
        External: true,
      } as UserRegister,
      researchOrganization: {} as ResearchOrganizationRegister,
      termsOfUse: false,
      registerUserAndOrganization: {
        researchOrganizationPostRequest: {} as ResearchOrganizationRegister,
        userPostRequest: {} as UserRegister,
      },
      submitted: false,

      researchInstitutions: [] as Array<any>,
      researchInstitutionId: "",
    });

    const onResearchInstitutionChange = (event: any) => {
      state.user.researchInstitutionMSTID = event.value;
    };

    const onResearchInstitutionFilterChange = (event: any) => {
      const searchValue = event.value;
      if (searchValue.length < 2) {
        return;
      }
      let apiURL = `api/v1/Categories/externalResearchInstitutions/${searchValue}`;

      axios.get(apiURL).then((response: any) => {
        state.researchInstitutions = response.data;
      });
    };

    const requiredIfOrganization = (value: string) =>
      !state.showOrganizationForm ||
      (value != null && value != undefined && value != "");

    const requiredTrue = (value: boolean) => state.gdprConsentEnabled;

    const rules = computed(() => ({
      user: {
        firstName: {
          required,
        },
        lastName: {
          required,
        },
        email: {
          required,
        },
        phoneNumber: {
          required,
        },
        researcherStatusId: {
          required,
        },
        researchInstitutionMSTID: {
          required,
        },
        username: {
          required,
        },
        password: {
          required,
        },
        passwordRepeat: {
          required,
        },
      },
      gdprConsentEnabled: {
        requiredTrue,
      },
      consentEnabled: {
        requiredTrue,
      },
      researchOrganization: {
        name: {
          requiredIfOrganization,
        },
        cityId: {
          requiredIfOrganization,
        },
      },
    }));
    const v$ = useVuelidate(rules, state);

    const titleOptions = [{ name: "Mr." }, { name: "Mrs." }, { name: "Ms." }];
    const languageOptions = [{ name: "Slovenia" }, { name: "English" }];

    const { researchOrganizationState } = ResearchOrganizationRepositoryAPI();
    const { getResearchEquipmentCategories } = ResearchCategoryFiltersData();

    const close = () => {
      context.emit("close");
    };

    const notificationDisplay = (
      severity: string,
      summary: string,
      detail?: string
    ): void => {
      toast.add({
        severity: severity,
        summary: summary,
        detail: detail,
        life: 3000,
      });
    };

    const onClickRegister = (): void => {
      v$.value.$validate();
      if (v$.value.$error) {
        state.submitted = true;
        return;
      }

      if (state.gdprConsentEnabled) {
        state.user.GDPRConsent = "APPROVED";
      }
      state.registerUserAndOrganization = {
        userPostRequest: state.user,
        researchOrganizationPostRequest: state.researchOrganization,
      };

      if (state.showOrganizationForm) {
        axios
          .post(
            "/api/v1/User/organization/register",
            state.registerUserAndOrganization
          )
          .then((response: any) => {
            if (response.status === 201) {
              notificationDisplay(
                "success",
                "Registracija organizacije uspešna"
              );
              close();
              return;
            }
            notificationDisplay("warn", "Registracija organizacije neupešna");
          })
          .catch((err: AxiosResponse) => {
            console.error(err);
            notificationDisplay("error", "Napaka zahteve");
          });
      } else {
        state.user.createdDate = getISODateString();
        axios
          .post("/api/v1/User/register", state.user)
          .then((response: any) => {
            if (response.status === 201) {
              notificationDisplay("success", "Registracija uporabnika uspešna");
              close();
              return;
            }
            notificationDisplay("warn", "Registracija uporabnika neuspešna");
          })
          .catch((err: AxiosResponse) => {
            console.error(err);
            notificationDisplay("error", "Napaka zahteve");
          });
      }
    };

    const onClickLogin = () => {
      keycloak.login({ redirectUri: window.origin });
    };

    return {
      t,
      state,
      location,
      onClickRegister,
      researchOrganizationState,
      titleOptions,
      languageOptions,
      getResearchEquipmentCategories,
      v$,
      onResearchInstitutionChange,
      onResearchInstitutionFilterChange,
      onClickLogin,
      getLogoByLocale: computed(() => getLogoPath()),
    };
  },
});
